// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { CheckmarkOutline, User, WarningAltFilled } from '@carbon/icons-react';
import type { CarbonIconType } from '@carbon/icons-react';
import { useSiteTime } from '../../../hooks/useSiteTime';

/**
 * Control handoff · CSV to PMS — read-only rollup of the Operator's manual
 * handoff for one plan.
 *
 * The steps below are HARDCODED PLACEHOLDERS. No endpoint publishes control
 * handoff state today: HITL carries the approval decision, the object store
 * carries the plan narration, and neither records that a CSV was exported,
 * keyed into the PMS, or attested to. Wiring needed to make this real:
 *
 *   TODO(work-order-service): the handoff is a work order against the plan —
 *   export, manual entry, attestation, telemetry confirmation. Once the WOMS
 *   exposes per-plan handoff/attestation steps, replace PLACEHOLDER_STEPS with
 *   a `useWorkOrders`-style hook over `services/workOrderService.ts` keyed on
 *   the plan's approvalId (the current WOMS contract has no such link — see
 *   `front/src/types/workOrder.ts`, where `originating_task` is the only
 *   provenance field and is always null in list responses). Until then this
 *   panel is illustrative only, which the footer note states in the UI.
 */

type HandoffState = 'verified' | 'manual' | 'flagged';

interface HandoffStep {
  key: string;
  num: number;
  title: string;
  statusLabel: string;
  state: HandoffState;
  /** Wall-clock time of day, [hour, minute] in 24h, stamped onto today. */
  at: [number, number];
  who: string;
}

/**
 * Placeholder steps carry a time of day, not a date: the handoff belongs to the
 * plan executing today, so the rows read as today's afternoon rather than a
 * frozen date drifting further into the past. Real data will bring its own
 * timestamps.
 */
function todayAt([hour, minute]: [number, number], today: string): string {
  // From the literal hour, not an instant: shifting a made-up time would print an unasked-for hour.
  return `${today} · ${hour}:${String(minute).padStart(2, '0')}`;
}

/**
 * One entry per state so the reader can tell an automatic confirmation from a
 * human attestation from a step that needs attention.
 */
const STATE_STYLE: Record<HandoffState, { color: string; Icon: CarbonIconType; label: string }> = {
  verified: { color: 'var(--st-ok)', Icon: CheckmarkOutline, label: 'Verified' },
  manual: { color: 'var(--text-helper)', Icon: User, label: 'Manual' },
  flagged: { color: 'var(--sev1fg)', Icon: WarningAltFilled, label: 'Needs attention' },
};

const PLACEHOLDER_STEPS: HandoffStep[] = [
  {
    key: 'exported',
    num: 1,
    title: 'CSV downloaded',
    statusLabel: 'Auto-logged on export',
    state: 'verified',
    at: [13, 52],
    who: 'Operator',
  },
  {
    key: 'applied',
    num: 2,
    title: 'Applied to PMS',
    statusLabel: 'Manual · attested by Operator',
    state: 'manual',
    at: [13, 58],
    who: 'Operator',
  },
  {
    key: 'confirmed',
    num: 3,
    title: 'Confirmed by telemetry',
    statusLabel: 'Converged within ±0.3 °F of recommended',
    state: 'verified',
    at: [14, 11],
    who: 'EnergyFluo',
  },
  // Only here to show the third state. Drop this row once real handoff data
  // arrives — a live plan produces a flagged step or it does not.
  {
    key: 'drift',
    num: 4,
    title: 'Chiller staging outside tolerance',
    statusLabel: 'Staging differs from recommended by 1 unit',
    state: 'flagged',
    at: [14, 24],
    who: 'EnergyFluo',
  },
];

function PlanControlHandoff() {
  const today = useSiteTime().format(new Date(), 'dateShort');
  return (
    <div className="pd-handoff">
      <div className="pd-handoff-title">Control handoff · CSV to PMS</div>
      <p className="pd-handoff-lede">
        EnergyFluo has no write access to the PMS. Setpoint and staging changes are exported for the
        Operator to key in manually — this tracks the handoff. Read-only rollup of the
        Operator&apos;s actions.
      </p>
      <div className="pd-handoff-steps">
        {PLACEHOLDER_STEPS.map(step => {
          const { color, Icon, label } = STATE_STYLE[step.state];
          return (
            <div className="pd-handoff-step" key={step.key} style={{ borderLeftColor: color }}>
              <span className="pd-handoff-icon" style={{ color }}>
                <Icon size={16} aria-label={label} />
              </span>
              <span className="pd-handoff-body">
                <span className="pd-handoff-step-title">
                  <span className="pd-handoff-num">{step.num}</span>
                  {step.title}
                </span>
                <span className="pd-handoff-status" style={{ color }}>
                  {step.statusLabel}
                </span>
              </span>
              <span className="pd-handoff-meta">
                {todayAt(step.at, today)}
                <br />
                {step.who}
              </span>
            </div>
          );
        })}
      </div>
      <p className="pd-handoff-note">
        Illustrative steps only — no service publishes control handoff state yet. Hooking this up to
        the work order service will make it real; the tolerance band (±0.3 °F setpoint, ±1 chiller
        staging) and the 20-min grace window are placeholder config, pending the control-tolerance
        owner.
      </p>
    </div>
  );
}

export default PlanControlHandoff;
